import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, CmsCase } from '@/lib/supabase';
import { cases as mockCases } from '@/mocks/caseData';
import { useScrollAnimation, useStaggerAnimation } from '@/hooks/useScrollAnimation';

export default function CaseStudies() {
  const [cases, setCases] = useState<CmsCase[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const navigate = useNavigate();

  const { ref: headerRef, isVisible: headerVisible } = useScrollAnimation();
  const { ref: cardsRef, visibleItems } = useStaggerAnimation(4, { threshold: 0.05 });

  useEffect(() => {
    const fetchCases = async () => {
      const { data, error } = await supabase
        .from('cms_cases')
        .select('id, title, client, industry, cover_image, logo_url, tags, metric_value, metric_label, slug')
        .eq('is_published', true)
        .order('sort_order', { ascending: true });

      if (error || !data || data.length === 0) {
        setCases(
          mockCases.map((c, i) => ({
            id: i + 1,
            title: c.title,
            client: c.client,
            industry: c.industry,
            summary: '',
            content: '',
            cover_image: c.image,
            hero_bg: c.heroBg,
            logo_url: c.logo,
            tags: c.tags,
            metric_value: c.metric,
            metric_label: c.metricLabel,
            slug: c.id,
            overview: c.overview,
            project_date: c.projectDate,
            challenges: c.challenges,
            solutions: c.solutions,
            results: c.results,
            testimonial: c.testimonial,
            sort_order: i + 1,
            is_published: true,
            created_at: '',
            updated_at: '',
          }))
        );
      } else {
        setCases(data as CmsCase[]);
      }
      setLoading(false);
    };

    fetchCases();
  }, []);

  const displayCases = loading
    ? mockCases.map((c, i) => ({
        id: i + 1,
        title: c.title,
        client: c.client,
        industry: c.industry,
        summary: '',
        content: '',
        cover_image: c.image,
        hero_bg: c.heroBg,
        logo_url: c.logo,
        tags: c.tags,
        metric_value: c.metric,
        metric_label: c.metricLabel,
        slug: c.id,
        overview: c.overview,
        project_date: c.projectDate,
        challenges: c.challenges,
        solutions: c.solutions,
        results: c.results,
        testimonial: c.testimonial,
        sort_order: i + 1,
        is_published: true,
        created_at: '',
        updated_at: '',
      }))
    : cases;

  const visibleCases = displayCases.slice(0, 4);
  const totalSlides = Math.ceil(displayCases.length / 4);

  const nextSlide = () => setCurrentIndex((prev) => (prev + 1) % Math.max(totalSlides, 1));
  const prevSlide = () => setCurrentIndex((prev) => (prev - 1 + Math.max(totalSlides, 1)) % Math.max(totalSlides, 1));

  const handleCaseClick = (slug: string) => navigate(`/case/${slug}`);

  return (
    <section id="cases" className="py-10 md:py-20 lg:py-24 bg-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 md:px-6 lg:px-8">
        <div
          ref={headerRef}
          className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 md:mb-12 gap-3 md:gap-4"
          style={{
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            opacity: headerVisible ? 1 : 0,
            transform: headerVisible ? 'translateY(0)' : 'translateY(40px)',
          }}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">成功案例</h2>
          <div className="flex gap-2">
            <button
              onClick={prevSlide}
              className="w-9 h-9 md:w-12 md:h-12 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-teal-600 hover:text-teal-600 transition-colors cursor-pointer"
              aria-label="上一个"
            >
              <i className="ri-arrow-left-line text-base md:text-xl" />
            </button>
            <button
              onClick={nextSlide}
              className="w-9 h-9 md:w-12 md:h-12 rounded-full border-2 border-gray-300 flex items-center justify-center hover:border-teal-600 hover:text-teal-600 transition-colors cursor-pointer"
              aria-label="下一个"
            >
              <i className="ri-arrow-right-line text-base md:text-xl" />
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {Array.from({ length: Math.max(totalSlides, 1) }).map((_, slideIdx) => {
              const slideItems = displayCases.slice(slideIdx * 4, slideIdx * 4 + 4);
              return (
                <div key={slideIdx} className="w-full flex-shrink-0 px-0.5 md:px-3">
                  <div
                    ref={slideIdx === 0 ? cardsRef : undefined}
                    className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6"
                  >
                    {slideItems.map((item, idx) => (
                      <div
                        key={item.id}
                        className="group relative rounded-xl md:rounded-3xl overflow-hidden cursor-pointer h-[220px] sm:h-[300px] md:h-[420px] lg:h-[500px]"
                        style={{
                          transition: `opacity 0.7s ease ${idx * 120}ms, transform 0.7s ease ${idx * 120}ms`,
                          opacity: visibleItems[idx] ? 1 : 0,
                          transform: visibleItems[idx] ? 'translateY(0)' : 'translateY(64px)',
                        }}
                        onClick={() => handleCaseClick(item.slug)}
                      >
                        <img
                          src={item.cover_image}
                          alt={item.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                        {item.logo_url && (
                          <div className="absolute top-2.5 left-2.5 md:top-6 md:left-6 bg-white/90 backdrop-blur-sm px-2 py-1 md:px-4 md:py-2 rounded-md md:rounded-lg">
                            <img src={item.logo_url} alt="Logo" className="h-4 sm:h-5 md:h-8 w-auto" />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 p-3 md:p-6 text-white">
                          <div className="flex gap-1 md:gap-2 mb-1.5 md:mb-3 flex-wrap">
                            {(item.tags || []).map((tag: string, tagIdx: number) => (
                              <span key={tagIdx} className="px-1.5 md:px-3 py-0.5 md:py-1 bg-white/20 backdrop-blur-sm rounded-full text-xs font-medium">
                                {tag}
                              </span>
                            ))}
                          </div>
                          <h3 className="text-xs sm:text-sm md:text-lg lg:text-xl font-bold mb-1.5 md:mb-4 line-clamp-2">{item.title}</h3>
                          <div className="mb-2 md:mb-4">
                            <div className="text-lg sm:text-xl md:text-3xl font-bold">{item.metric_value}</div>
                            <div className="text-xs text-white/80">{item.metric_label}</div>
                          </div>
                          <button className="px-3 md:px-6 py-1 md:py-2 border border-white md:border-2 rounded-full text-xs font-medium hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap">
                            查看详情
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
